import * as XLSX from 'xlsx';

/**
 * Export data to Excel file with multiple sheets
 * @param {Object} config - Export configuration
 * @param {string} config.filename - Name of the exported file (without extension)
 * @param {Array} config.sheets - Array of sheet configurations
 * @param {string} config.sheets[].name - Sheet name
 * @param {Array} config.sheets[].data - Array of objects to export
 * @param {Array} config.sheets[].columns - Optional column definitions [{header: 'Display Name', key: 'dataKey'}]
 */
export const exportToExcel = ({ filename, sheets }) => {
  const workbook = XLSX.utils.book_new();

  sheets.forEach(({ name, data, columns }) => {
    if (!data || data.length === 0) {
      // Create empty sheet with headers only
      const ws = XLSX.utils.aoa_to_sheet([columns ? columns.map(c => c.header) : ['No data available']]);
      XLSX.utils.book_append_sheet(workbook, ws, name.substring(0, 31)); // Excel sheet names max 31 chars
      return;
    }

    let sheetData;
    
    if (columns) {
      // Use custom column mapping
      const headers = columns.map(c => c.header);
      const rows = data.map(row => columns.map(c => row[c.key] ?? ''));
      sheetData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      
      // Auto-size columns
      const colWidths = headers.map((header, i) => {
        const maxLength = Math.max(
          header.length,
          ...rows.map(row => String(row[i] ?? '').length)
        );
        return { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, ws, name.substring(0, 31));
    } else {
      // Auto-detect columns from data
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Auto-size columns
      const keys = Object.keys(data[0] || {});
      ws['!cols'] = keys.map(key => {
        const maxLength = Math.max(
          key.length,
          ...data.map(row => String(row[key] ?? '').length)
        );
        return { wch: Math.min(maxLength + 2, 50) };
      });
      
      XLSX.utils.book_append_sheet(workbook, ws, name.substring(0, 31));
    }
  });

  // Generate and download
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Export Retention & Growth metrics - all data on one sheet
 */
export const exportRetentionMetrics = (data, dateRange) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const workbook = XLSX.utils.book_new();
  
  // Build a single sheet with all sections
  const rows = [];
  
  // === 7-Day Retention Section ===
  rows.push(['7-DAY RETENTION']);
  rows.push(['Cohort Date', 'Cohort Size', 'Retained Users', 'Retention Rate (%)']);
  if (data.retention7d?.retention_data?.length > 0) {
    data.retention7d.retention_data.forEach(item => {
      rows.push([item.cohort_date, item.cohort_size, item.retained_users, item.retention_rate]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]); // Empty row separator
  
  // === 30-Day Retention Section ===
  rows.push(['30-DAY RETENTION']);
  rows.push(['Cohort Date', 'Cohort Size', 'Retained Users', 'Retention Rate (%)']);
  if (data.retention30d?.retention_data?.length > 0) {
    data.retention30d.retention_data.forEach(item => {
      rows.push([item.cohort_date, item.cohort_size, item.retained_users, item.retention_rate]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === DAU/MAU Stickiness Section ===
  rows.push(['DAU/MAU STICKINESS']);
  rows.push(['Date', 'Daily Active Users', 'Monthly Active Users', 'Stickiness (%)']);
  if (data.dauMauStickiness?.metrics_data?.length > 0) {
    data.dauMauStickiness.metrics_data.forEach(item => {
      rows.push([item.date, item.dau, item.mau, item.stickiness]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === Power User Segments Section ===
  rows.push(['POWER USER SEGMENTS']);
  rows.push(['Segment', 'User Count', 'Percentage (%)']);
  if (data.powerUserSegmentation?.segmentation_data?.length > 0) {
    data.powerUserSegmentation.segmentation_data.forEach(item => {
      rows.push([item.segment, item.user_count, item.percentage]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === Practice Retention Section ===
  rows.push(['PRACTICE RETENTION']);
  rows.push(['Week', 'Active Practices', 'Returning Practices', 'Retention Rate (%)']);
  if (data.practiceRetention?.retention_data?.length > 0) {
    data.practiceRetention.retention_data.forEach(item => {
      rows.push([item.week, item.active_practices, item.returning_practices, item.retention_rate]);
    });
  } else {
    rows.push(['No data available']);
  }
  
  const ws = XLSX.utils.aoa_to_sheet(rows);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, ws, 'Retention & Growth');
  XLSX.writeFile(workbook, `Retention_Growth_Metrics_${dateRange}_${timestamp}.xlsx`);
};

/**
 * Export Practice Performance metrics - all data on one sheet
 */
export const exportPracticeMetrics = (data, dateRange) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const workbook = XLSX.utils.book_new();
  
  const rows = [];
  
  // === Active Practices Section ===
  rows.push(['ACTIVE PRACTICES']);
  rows.push(['Practice Name', 'Total Checks', 'Unique Users', 'Avg Response Time (ms)']);
  if (data.activePractices?.practices?.length > 0) {
    data.activePractices.practices.forEach(item => {
      rows.push([item.practice_name, item.total_checks, item.unique_users, item.avg_response_time]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === Practice Retention Section ===
  rows.push(['PRACTICE RETENTION']);
  rows.push(['Week', 'Active Practices', 'New Practices', 'Churned Practices', 'Retention Rate (%)']);
  if (data.practiceLevelRetention?.retention_data?.length > 0) {
    data.practiceLevelRetention.retention_data.forEach(item => {
      rows.push([item.week, item.active_practices, item.new_practices, item.churned_practices, item.retention_rate]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === Usage Tiers Section ===
  rows.push(['USAGE TIERS']);
  rows.push(['Tier', 'Practice Count', 'Percentage (%)', 'Total Checks']);
  if (data.practicesByUsageTier?.tier_data?.length > 0) {
    data.practicesByUsageTier.tier_data.forEach(item => {
      rows.push([item.tier || item.usage_tier, item.practice_count, item.percentage, item.total_checks]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === Engagement Trends Section ===
  rows.push(['ENGAGEMENT TRENDS']);
  rows.push(['Week', 'Total Practices', 'Total Checks', 'Avg Checks/Practice']);
  if (data.practiceEngagementTrends?.trends_data?.length > 0) {
    data.practiceEngagementTrends.trends_data.forEach(item => {
      rows.push([item.week_start, item.active_practices, item.total_checks, item.avg_checks_per_practice]);
    });
  } else {
    rows.push(['No data available']);
  }
  
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [
    { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, ws, 'Practice Performance');
  XLSX.writeFile(workbook, `Practice_Performance_Metrics_${dateRange}_${timestamp}.xlsx`);
};

/**
 * Export Workflow Adoption metrics - all data on one sheet
 */
export const exportWorkflowMetrics = (data, dateRange) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const workbook = XLSX.utils.book_new();
  
  const rows = [];
  
  // === Checks by Role Section ===
  rows.push(['COVERAGE CHECKS BY ROLE']);
  rows.push(['Role', 'Checks Initiated', 'Checks Completed', 'Unique Users', 'Avg Response Time (ms)']);
  if (data.coverageChecksByRole?.role_breakdown?.length > 0) {
    data.coverageChecksByRole.role_breakdown.forEach(item => {
      rows.push([
        item.role_full_name || item.initiated_by_role, 
        item.checks_initiated || item.check_count, 
        item.checks_completed, 
        item.unique_users, 
        item.avg_response_time
      ]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === Feature Module Adoption Section ===
  rows.push(['FEATURE MODULE ADOPTION']);
  rows.push(['Module', 'Total Views', 'Unique Users', 'Adoption Rate (%)', 'Avg Time Spent (sec)']);
  if (data.featureModuleAdoption?.feature_adoption?.length > 0) {
    data.featureModuleAdoption.feature_adoption.forEach(item => {
      rows.push([
        item.feature_module, 
        item.view_count, 
        item.unique_users, 
        item.percentage, 
        item.avg_time_spent_sec || (item.avg_time_spent_ms ? (item.avg_time_spent_ms / 1000).toFixed(2) : '')
      ]);
    });
  } else {
    rows.push(['No data available']);
  }
  rows.push([]);
  
  // === API Call Frequency Section ===
  rows.push(['API CALL FREQUENCY']);
  rows.push(['Date', 'Coverage Checks', 'Total API Calls', 'Unique Users', 'Avg Response Time (ms)']);
  if (data.apiCallFrequency?.frequency_data?.length > 0) {
    data.apiCallFrequency.frequency_data.forEach(item => {
      rows.push([
        item.call_date || item.date, 
        item.coverage_checks, 
        item.api_calls, 
        item.unique_users, 
        item.avg_response_time
      ]);
    });
  } else {
    rows.push(['No data available']);
  }
  
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [
    { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 22 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, ws, 'Workflow Adoption');
  XLSX.writeFile(workbook, `Workflow_Adoption_Metrics_${dateRange}_${timestamp}.xlsx`);
};
